import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainContentLayout from "../../components/mainContentLayout";
import PageHeader from "../../components/pageHeader";
import LoadingOverlay from "../../components/loadingOverlay";
import { useToast } from "../../hooks/useToast";
import { API_BASE } from "../../config/apiConfig";
import { FaCheck, FaTimes } from "react-icons/fa";

export default function CoachLeaveForm({ isEdit }) {
  const navigate = useNavigate();
  const { id } = useParams();   // for edit mode
  const token = sessionStorage.getItem("access_token");
  const { showToast, ToastContainer } = useToast();

  const todayStr = new Date().toISOString().split("T")[0];

  // Form fields
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [duration, setDuration] = useState(1);
  const [reasonId, setReasonId] = useState("");
  const [remark, setRemark] = useState("");

  // Reasons
  const [reasons, setReasons] = useState([]);

  const [loading, setLoading] = useState(false);

  // -------------------------------------------------------
  // Fetch reasons
  // -------------------------------------------------------
  async function fetchReasons() {
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-reasons/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        setReasons(data.reasons || []);
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to load reasons", "error");
    }
  }

  // -------------------------------------------------------
  // Fetch existing leave detail (edit mode)
  // -------------------------------------------------------
  async function fetchLeaveDetail() {
    if (!isEdit || !id) return;

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/coach/leave/detail/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        const d = data.item;

        // Convert timestamps to YYYY-MM-DD
        const sDate = d.startDate?.seconds
          ? new Date(d.startDate.seconds * 1000)
          : new Date(d.startDate);

        const eDate = d.endDate?.seconds
          ? new Date(d.endDate.seconds * 1000)
          : new Date(d.endDate);

        setStartDate(sDate.toISOString().split("T")[0]);
        setEndDate(eDate.toISOString().split("T")[0]);

        // duration = difference + 1
        const diff = Math.round((eDate - sDate) / (1000 * 60 * 60 * 24)) + 1;
        setDuration(diff);

        setReasonId(d.reasonId);
        setRemark(d.remark || "");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to load record", "error");
    } finally {
      setLoading(false);
    }
  }

  // -------------------------------------------------------
  // Auto update endDate from duration
  // -------------------------------------------------------
  useEffect(() => {
    if (startDate && duration > 1) {
      const s = new Date(startDate);
      s.setDate(s.getDate() + duration - 1);
      setEndDate(s.toISOString().split("T")[0]);
    } else {
      setEndDate(startDate);
    }
  }, [startDate, duration]);

  // User manually changes endDate → update duration
  function handleEndDateChange(v) {
    setEndDate(v);
    const s = new Date(startDate);
    const e = new Date(v);
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
    if (diff > 0) setDuration(diff);
  }

  // -------------------------------------------------------
  // Submit form
  // -------------------------------------------------------
  async function handleSubmit() {
    if (!startDate || !reasonId) return;

    const selectedReason = reasons.find(r => r.id === reasonId);
    if (selectedReason?.label === "Others" && !remark.trim()) {
      return showToast("Remark is required when selecting Others", "error");
    }

    setLoading(true);

    try {
      const body = {
        startDate,
        endDate,
        reasonId,
        reasonLabel: selectedReason.label,
        remark,
        duration,
      };

      let res;
      if (isEdit) {
        // update
        res = await fetch(`${API_BASE}/api/coach/leave/update/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body),
        });
      } else {
        // create
        res = await fetch(`${API_BASE}/api/coach/leave/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body),
        });
      }

      const data = await res.json();
      if (data.success) {
        showToast("Saved successfully!", "success");
        navigate("/coach-leaves-list");
      } else {
        showToast(data.message || "Save failed", "error");
      }

    } catch (err) {
      console.error(err);
      showToast("Save failed", "error");
    } finally {
      setLoading(false);
    }
  }

  // INIT
  useEffect(() => {
    fetchReasons();
    fetchLeaveDetail();
  }, []);

  return (
    <MainContentLayout>
      <PageHeader
        title={isEdit ? "Edit Leave" : "Apply Leave"}
        backTo="/coach-leaves-list"
      />

      <div className="bg-white p-1 text-left">
        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleSubmit}
            disabled={!startDate || !reasonId || loading || (reasons.find(r => r.id === reasonId)?.label === "Others" && !remark.trim())}
            className={`p-2 bg-blue-600 text-white rounded ${
              (!startDate || !reasonId || loading || (reasons.find(r => r.id === reasonId)?.label === "Others" && !remark.trim()))
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            title={loading ? "Saving..." : "Save"}
          >
            <FaCheck size={20} />
          </button>

          <button
            onClick={() => navigate("/coach-leaves-list")}
            className="p-2 bg-gray-300 rounded"
            title="Cancel"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <label className="block mb-1 font-bold">
          Start Date <span className="text-red-500 italic text-xs">*Required</span>
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="border rounded p-2 w-full mb-4"
        />

        <label className="block mb-1 font-bold">
          Duration (days) <span className="text-red-500 italic text-xs">*Required</span>
        </label>
        <input
          type="number"
          min="1"
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          className="border rounded p-2 w-full mb-4"
        />

        <label className="block mb-1 font-bold">
          End Date <span className="text-red-500 italic text-xs">*Required</span>
        </label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          className="border rounded p-2 w-full mb-4"
        />

        <label className="block mb-1 font-bold">
          Reason <span className="text-red-500 italic text-xs">*Required</span>
        </label>
        <select
          value={reasonId}
          onChange={(e) => setReasonId(e.target.value)}
          className="border rounded p-2 w-full mb-4"
        >
          <option value="">Select reason</option>
          {reasons.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>

        <label className="block mb-1 font-bold">
          Remark 
          {reasons.find(r => r.id === reasonId)?.label === "Others" ? (
            <span className="text-red-500 italic text-xs">*Required</span>
          ) : (
            <span className="italic text-gray-400 text-xs">(Optional)</span>
          )}

        </label>
        <textarea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Optional remarks..."
          className="border rounded p-2 w-full mb-4 placeholder:italic placeholder:text-gray-400"
        />
      </div>

      <ToastContainer />
      <LoadingOverlay show={loading} text="Processing..." />
    </MainContentLayout>
  );
}
